import { SettingRow } from "@/lib/shopConfigAudit";
import { formatAuditValue } from "@/lib/shopConfigAuditView";
import { cn } from "@/lib/utils";
import { STATUS_STYLES } from "./statusStyles";

const GRID = "grid grid-cols-[minmax(0,1fr)_96px_120px] gap-3";

const Swatch = ({ color }: { color?: string }) =>
  color ? (
    <span
      data-swatch
      aria-hidden
      className="mr-1.5 inline-block h-3 w-3 shrink-0 rounded-sm border border-slate-300 align-middle"
      style={{ backgroundColor: color }}
    />
  ) : null;

const ValueCell = ({ row }: { row: SettingRow }) => {
  if (row.status === "missing") {
    return <span className={STATUS_STYLES.missing.valuePill}>Not set</span>;
  }
  return (
    <span className={STATUS_STYLES[row.status].valuePill}>
      <Swatch color={row.swatch?.actual} />
      {formatAuditValue(row.actual)}
    </span>
  );
};

export const SettingsGrid = ({ rows }: { rows: SettingRow[] }) => {
  const groups = rows.reduce<{ label: string; rows: SettingRow[] }[]>(
    (acc, row) => {
      const group = acc.find((g) => g.label === row.group);
      if (group) group.rows.push(row);
      else acc.push({ label: row.group, rows: [row] });
      return acc;
    },
    []
  );

  return (
    <div className="text-foreground">
      <div
        className={cn(
          GRID,
          "bg-background px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
        )}
      >
        <div>Setting</div>
        <div>Default</div>
        <div>This shop</div>
      </div>
      {groups.map((group) => (
        <div key={group.label || "_"}>
          {group.label && (
            <div className="px-4 pb-1 pt-2.5 font-mono text-[11px] text-muted-foreground">
              {group.label}
            </div>
          )}
          {group.rows.map((row) => (
            <div
              key={row.path}
              className={cn(
                GRID,
                "items-center border-t border-border/60 px-4 py-2"
              )}
            >
              <div className="flex min-w-0 items-start gap-2">
                <span
                  aria-hidden
                  className={cn(
                    "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                    STATUS_STYLES[row.status].dot
                  )}
                />
                <div className="flex min-w-0 flex-col gap-px">
                  <span className="text-[13px] text-foreground">
                    {row.label}
                  </span>
                  <span className="break-all font-mono text-[10.5px] text-muted-foreground">
                    {row.path}
                  </span>
                </div>
              </div>
              <div className="break-words text-[13px] text-muted-foreground">
                <Swatch color={row.swatch?.expected} />
                {formatAuditValue(row.expected)}
              </div>
              <div className="flex">
                <ValueCell row={row} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
